/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState } from 'react';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import axiosCall from '../../services/axios';
import { getErrorFromEndpoint, isObjEmptyOrNull, isStrEmptyOrNull } from '../../common/UtilityFunctions';

const useDocumentESign = () => {
  const { token } = useAuthContext();
  const { get } = useAbortController();
  const [ESignResp, setESignResp] = useState(null);
  const [eSignErrors, setESignErrors] = useState('');
  const [eSignLoading, setESignLoading] = useState(false);

  const ESignAPI = useCallback(
    async (params: ESignRequest): Promise<UnknownESignResponse> => {
      const urlPath = process.env.REACT_APP_ESIGN;
      const request = { url: urlPath, data: params, method: 'post' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();

      const res = await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            const err = getErrorFromEndpoint(res);
            if (isStrEmptyOrNull(err)) {
              setESignResp(res);
              return res;
            } else {
              console.log(err);
              setESignErrors(err);
              setESignResp(null);
              return null;
            }
          }
          return null;
        })
        .catch((e: Error) => {
          console.log('Error using ESignAPI in the useBatch hook: ', e);
          setESignErrors(e.message);
          setESignResp(null);
          return null;
        });
      setESignLoading(false);
      return res;
    },
    [get, token],
  );

  const processESignRequest = useCallback(
    async (params: ESignRequest) => {
      setESignResp(null);
      if (!isObjEmptyOrNull(params)) {
        setESignLoading(true);
        const res = await ESignAPI(params);
        return res;
      }
      return null;
    },
    [ESignAPI],
  );

  return { ESignResp, eSignLoading, eSignErrors, processESignRequest };
};

export default useDocumentESign;
